import { createAction, props } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { DatasetShareSetting, AvailableSubscription, ExportTemplate } from './dataset.model';
import { Dataset, DatasetMappings } from 'src/app/modules/core/store/models/dataset.model';
import { DocumentSchema, FieldDataType, SchemaField, SystemFieldType } from 'src/app/modules/core/store/models/document.model';
import { QueryParams, QueryResult } from 'src/app/modules/core/store/models/query.model';

//
// Dataset Page actions
//
export const newDataset = createAction('[Dataset/Page] New Dataset');
export const setSelectedDataset = createAction('[Dataset/Page] Set Selected Dataset', props<{ uId: string; skipNavigate?: boolean }>());
export const setSelectedDatasetComplete = createAction('[Dataset/Page] Set Selected Dataset Complete', props<{ uId: string }>());
export const setDatasetForDelete = createAction('[Dataset/Page] Set Selected Dataset For Delete', props<{ uId: string }>());
export const clearDatasetForDelete = createAction('[Dataset/Page] Clear Selected Dataset For Delete');
export const deleteDataset = createAction('[Dataset/Page] Delete Dataset', props<{ uId: string }>());
export const showDatasetProperties = createAction('[Dataset/Page] Show Dataset Properties', props<{ isEditMode: boolean }>());
export const hideDatasetProperties = createAction('[Dataset/Page] Hide Dataset Properties');
export const setSelectedEditDatasetSection = createAction('[Dataset/Page] Set Dataset Section', props<{ sectionName: string }>());
export const cancelEditDataset = createAction('[Dataset/Page] Cancel Edit Dataset');
export const cancelEditDatasetComplete = createAction('[Dataset/Page] Cancel Edit Dataset Complete');
export const setIsDatasetDirty = createAction('[Dataset/Page] Set Dataset Page Dirty', props<{ isDirty: boolean }>());

export const updateSelectedDatasetMappings = createAction(
  '[Dataset/Page] Update Selected Dataset',
  props<{ mappings: DatasetMappings[] }>()
);

export const loadSelectedDataset = createAction('[Dataset/Page] Load Selected Dataset', props<{ dataset: Dataset }>());

//
// Dataset Field actions
//
export const showNewFieldSelect = createAction('[Dataset Field/Page] Show New Field Select');
export const hideNewFieldSelect = createAction('[Dataset Field/Page] Hide New Field Select');
export const addDatasetField = createAction(
  '[Dataset Field/Page] Add Dataset Field',
  props<{ dataType: FieldDataType; fieldType: SystemFieldType; cultureCode?: string }>()
);
export const deleteDatasetField = createAction('[Dataset Field/Page] Delete Dataset Field');
export const cancelEditDatasetField = createAction('[Dataset Field/Page] Cancel Edit Dataset Field');
export const cancelDeleteDatasetField = createAction('[Dataset Field/Page] Cancel Delete Dataset Field');
export const setSelectedDatasetField = createAction('[Dataset Field/Page] Set Selected Dataset Field', props<{ uId: string }>());
export const setSelectedDatasetFieldForDelete = createAction(
  '[Dataset Field/Page] Set Selected Dataset Field For Delete',
  props<{ uId: string }>()
);
export const updateDatasetField = createAction('[Dataset Field/Page] Update Dataset Field', props<{ field: SchemaField }>());
export const updateDatasetFields = createAction('[Dataset Field/Page] Update Dataset Fields', props<{ fields: SchemaField[] }>());

//
// Dataset API actions
//
export const addDataset = createAction('[Dataset/API] Add Set', props<{ dataset: Dataset }>());
export const createDataset = createAction('[Dataset/API] Create Dataset', props<{ dataset: Dataset }>());
export const createDatasetComplete = createAction('[Dataset/API] Create Dataset Complete', props<{ dataset: Dataset }>());
export const getDatasets = createAction('[Dataset/API] Get Dataset', props<{ params: Partial<QueryParams>; includeWorkflow?: boolean }>());
export const deleteSelectedDataset = createAction('[Dataset/API] Delete Selected Dataset');
export const loadDatasets = createAction('[Dataset/API] Load Datasets', props<{ results: QueryResult<Dataset> }>());
export const setError = createAction('[Dataset/API] Set Error', props<{ err: any }>());
export const updateDataset = createAction('[Dataset/API] Update Dataset', props<{ dataset: Dataset }>());
export const updateDatasetComplete = createAction('[Dataset/API] Update Dataset Complete', props<{ dataset: Update<Dataset> }>());
export const saveDataset = createAction('[Dataset/API] Save Dataset', props<{ dataset: Dataset }>());
export const loadDatasetsForSelect = createAction('[Dataset/API] Load Datasets For Select', props<{ includeWorkflow?: boolean }>());
export const loadDatasetsForSelectComplete = createAction(
  '[Dataset/API] Load Datasets For Select Complete',
  props<{ results: QueryResult<Dataset> }>()
);
export const loadDatasetSchemaWithTranslations = createAction('[Dataset Field/Api] Load dataset schema with translations');
export const loadDatasetSchemaWithTranslationsComplete = createAction(
  '[Dataset Field/Api] Load dataset schema with translations Complete',
  props<{ schema: DocumentSchema }>()
);

// Dataset sharing
//
export const getDatasetShareSettingsFromApi = createAction(
  '[Dataset/API] Get Dataset Share Settings from Api',
  props<{ params: Partial<QueryParams> }>()
);
export const getDatasetShareSettingsFromApiComplete = createAction(
  '[Dataset/API] Get Dataset Share Settings from Api Complete',
  props<{ results: QueryResult<DatasetShareSetting> }>()
);
export const shareDataset = createAction('[Dataset/API] Share Dataset', props<{ organizationUId: string; datasetUId: string }>());
export const shareDatasetComplete = createAction('[Dataset/API] Share Dataset Complete');
export const unshareDataset = createAction('[Dataset/API] Unshare Dataset', props<{ organizationUId: string; datasetUId: string }>());
export const unshareDatasetComplete = createAction('[Dataset/API] Unshare Dataset Complete');
export const getAvailableSubscriptionsFromApi = createAction(
  '[Dataset/API] Get Available Subscriptions from Api',
  props<{ params: Partial<QueryParams> }>()
);
export const getAvailableSubscriptionsFromApiComplete = createAction(
  '[Dataset/API] Get Available Subscriptions from Api Complete',
  props<{ results: QueryResult<AvailableSubscription> }>()
);
export const subscribeToDataset = createAction('[Dataset/API] Subscribe to Dataset', props<{ datasetUId: string }>());

//
// Export Templates
//
export const loadDatasetExportTemplates = createAction(
  '[Dataset/API] Get Dataset Export Templates',
  props<{ params: Partial<QueryParams> }>()
);
export const loadDatasetExportTemplatesComplete = createAction(
  '[Dataset/API] Get Dataset Export Templates Complete',
  props<{ results: QueryResult<ExportTemplate> }>()
);
export const createExportTemplate = createAction('[Dataset/API] Create Dataset Export Template', props<{ template: ExportTemplate }>());
export const createExportTemplateComplete = createAction(
  '[Dataset/API] Create Dataset Export Template Complete',
  props<{ template: ExportTemplate }>()
);
export const updateExportTemplate = createAction('[Dataset/API] Update Dataset Export Template', props<{ template: ExportTemplate }>());
export const updateExportTemplateComplete = createAction(
  '[Dataset/API] Update Dataset Export Template Complete',
  props<{ template: ExportTemplate }>()
);
export const deleteExportTemplate = createAction('[Dataset/API] Delete Dataset Export Template', props<{ template: ExportTemplate }>());
export const deleteExportTemplateComplete = createAction(
  '[Dataset/API] Delete Dataset Export Template Complete',
  props<{ template: ExportTemplate }>()
);
export const showTemplateProperties = createAction('[Dataset/Page] Show Template Properties', props<{ template: ExportTemplate }>());
export const hideTemplateProperties = createAction('[Dataset/Page] Hide Template Properties');
